/**
 * ShiftAnalyticsService - per-employee shift statistics for the dashboard.
 */

import httpService from '../../../core/services/HttpService';

export interface EmployeeAnalytics {
  employee_id: number;
  first_name: string;
  last_name: string;
  applications_total: number;
  applications_weekend: number;
  hours_worked: number;
  weekend_hours_worked: number;
  weekend_shifts_worked: number;
  shifts_matched: number;
  punctual_count: number;
  late_count: number;
  max_start_deviation_min: number;
  overtime_min: number;
  time_trackings_count: number;
  change_requests_count: number;
  absence_days: number;
  absence_rate_pct: number;
}

export interface ShiftAnalyticsResponse {
  date_range: { start: string; end: string };
  late_threshold: number;
  employees: EmployeeAnalytics[];
}

export interface CalendarEvent {
  name: string;
  type: 'holiday' | 'event';
}

export interface AbsenceCalendarDay {
  date: string; // YYYY-MM-DD
  count: number;
  names: string[];
  events: CalendarEvent[];
}

export interface AbsenceCalendarResponse {
  date_range: { start: string; end: string };
  employee_count: number;
  days: AbsenceCalendarDay[];
}

export interface MonthOption {
  key: string;   // YYYY-MM
  label: string; // e.g. "June 2026"
  start: string; // YYYY-MM-DD (first day)
  end: string;   // YYYY-MM-DD (last day)
}

function toIso(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Builds a list of selectable months (most recent first), starting at the
 * reference month and going back `count` months.
 */
export function buildMonthOptions(count: number = 12, reference: Date = new Date()): MonthOption[] {
  const year = reference.getFullYear();
  const month = reference.getMonth();
  const options: MonthOption[] = [];

  for (let i = 0; i < count; i++) {
    const start = new Date(year, month - i, 1);
    const end = new Date(year, month - i + 1, 0);
    options.push({
      key: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`,
      label: `${MONTH_LABELS[start.getMonth()]} ${start.getFullYear()}`,
      start: toIso(start),
      end: toIso(end),
    });
  }

  return options;
}

export default class ShiftAnalyticsService {
  static async fetchAnalytics(start: string, end: string): Promise<ShiftAnalyticsResponse> {
    const response = await httpService.get<ShiftAnalyticsResponse>(
      `/api/admin/shift-analytics/?start=${start}&end=${end}`
    );
    const data = response.data as ShiftAnalyticsResponse;
    // Guard against non-JSON responses (e.g. SPA index.html when the endpoint
    // isn't deployed): such a 200 must surface as an error, not an empty dashboard.
    if (!data || !Array.isArray(data.employees)) {
      throw new Error('Invalid analytics response (endpoint not available?)');
    }
    return data;
  }

  static async fetchAbsenceCalendar(start: string, end: string): Promise<AbsenceCalendarResponse> {
    const response = await httpService.get<AbsenceCalendarResponse>(
      `/api/admin/absence-calendar/?start=${start}&end=${end}`
    );
    const data = response.data as AbsenceCalendarResponse;
    if (!data || !Array.isArray(data.days)) {
      throw new Error('Invalid absence-calendar response (endpoint not available?)');
    }
    return data;
  }
}
